/**
 * Sincronização dos custom claims do token.
 *
 * As permissões do usuário moram no banco *default*. As regras de segurança de
 * um banco **nomeado** (`cbtkdeventos`, `cbtkdeduca`, …) não conseguem lê-las:
 * `get()` em regra não atravessa bancos. O token, sim, chega em todos — por
 * isso as permissões que alguma regra precisa decidir viajam como claim.
 *
 * O servidor calcula as flags a partir do Firestore; aqui só pedimos o
 * recálculo e forçamos a renovação do token, para que o claim novo passe a
 * valer sem o usuário sair e entrar de novo.
 *
 * Nada aqui bloqueia login: falhar em sincronizar significa continuar com o
 * token anterior, que é exatamente o estado de antes desta funcionalidade
 * existir. Derrubar a autenticação por causa disso seria trocar um problema de
 * autorização por um de acesso.
 */

import { getAuth } from 'firebase/auth';
import AuthenticatedApiService from './AuthenticatedApiService';

/** Evita repetir a sincronização a cada re-render do contexto de autenticação. */
let ultimoUidSincronizado = null;

/**
 * Recalcula os claims do usuário logado e renova o token se algo mudou.
 *
 * @param {string} uid uid do usuário; usado só para não repetir na mesma sessão
 * @returns {Promise<Object|null>} resultado do servidor, ou null se não rodou
 */
export async function sincronizarClaims(uid) {
  if (!uid || ultimoUidSincronizado === uid) return null;
  ultimoUidSincronizado = uid;

  try {
    const resultado = await AuthenticatedApiService.post('/api/claims/sincronizar', {});

    // Só renova o token quando o servidor gravou algo. `getIdToken(true)` bate
    // no servidor de identidade do Firebase; fazer isso em toda abertura de
    // tela é chamada de rede que não paga o que custa.
    if (resultado?.gravado) {
      await getAuth().currentUser?.getIdToken(true);
    }

    return resultado;
  } catch (erro) {
    // Não propaga: ver o comentário do topo.
    console.warn('[Claims] Não foi possível sincronizar os claims:', erro?.message);
    ultimoUidSincronizado = null; // permite nova tentativa no próximo ciclo
    return null;
  }
}

/** Zera o controle de repetição. Chamar no logout. */
export function esquecerSincronizacaoClaims() {
  ultimoUidSincronizado = null;
}
